// ============================================================================
// DashboardPage — Overview (slice 3 of the redesign rollout).
//
// Composition (top→bottom, "what do I do now?" first):
//   1. OrientationHeader    greeting + plain-language market sentence
//   2. IndexStrip           SPY / QQQ / IWM / DIA cards row
//   3. SignalsHero          featured top-pick + ranked-list 2-col
//   4. Trust band           PortfolioCard (full-width for non-admins)
//                           + TrackRecordCard (admin-only per business rule)
//   5. MarketContext        SPY 30-day area chart + internals + movers
//
// All data wired to the existing APIs (apiGetPortfolio,
// apiGetPortfolioHistory, apiGetSignalPerformance, getMarketOverview,
// generateTradeSignals + validateSignalsBatch). No backend changes.
//
// The TrackRecordCard / signal backtest stats are gated on
// `user.role === 'admin'` per the critical business rule in
// design_handoff/README.md — non-admins never see realised win-rate
// numbers anywhere on this page.
// ============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useExchange } from '../context/ExchangeContext';
import { useAuth } from '../context/AuthContext';
import {
  generateTradeSignals, getMarketOverview, getHistory,
  isLiveData, getDataSource, applyCalibrationCurve,
} from '../utils/marketEngine';
import {
  apiGetPortfolio, apiGetPortfolioHistory,
  apiGetCalibrationCurvePublic,
} from '../utils/backendApi';
import { validateSignalsBatch } from '../utils/validateSignalsBatch';
import { TERMS } from '../utils/terms';
import { buildConfidenceTitle } from '../utils/confidenceBreakdown';
import CountUp from '../components/ui/CountUp';
import Icon from '../components/ui/Icon';
import ConfRing, { confColor } from '../components/ui/ConfRing';
import ConfBar from '../components/ui/ConfBar';
import DirBadge from '../components/ui/DirBadge';
import HealthDot from '../components/ui/HealthDot';
import { AreaSpark } from '../components/ui/Sparklines';
import PriceRail from '../components/ui/PriceRail';
import { cs, fmtMoney } from '../utils/format';

/* ════════════════════════════════════════════════════════════════════════
 * 1 · OrientationHeader — greeting + plain-language market sentence
 * ══════════════════════════════════════════════════════════════════════ */
function OrientationHeader({ user, market, signalCount, onRefresh, refreshing }) {
  const hour  = new Date().getHours();
  const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const idx   = market?.indices?.[0];
  const breadth = market?.advancers != null && market?.decliners != null
    ? Math.round((market.advancers / Math.max(1, market.advancers + market.decliners)) * 100)
    : null;
  const vixWord = market?.vix == null ? null
    : market.vix < 18 ? 'calm' : market.vix < 25 ? 'normal' : 'elevated';
  const pct    = idx?.changePct ?? 0;
  const tone   = pct >  0.2 ? { w: 'Bullish session', c: 'var(--green)' }
              : pct < -0.2 ? { w: 'Bearish session', c: 'var(--red)' }
              : { w: 'Quiet session', c: 'var(--amber)' };
  const now = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <header style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap', marginBottom: 'var(--gap)',
    }}>
      <div style={{ minWidth: 0, flex: '1 1 320px' }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>{now}</div>
        <h1 style={{
          fontSize: 'clamp(22px, 3.6vw, 30px)', fontWeight: 800,
          letterSpacing: '-0.03em', lineHeight: 1.05, color: 'var(--text-primary)',
          margin: 0,
        }}>
          {greet}, {firstName}
        </h1>
        <p style={{
          fontSize: 14, color: 'var(--text-secondary)',
          marginTop: 8, lineHeight: 1.55, maxWidth: 640, margin: '8px 0 0',
        }}>
          {idx ? (
            <>
              <span style={{ color: tone.c, fontWeight: 700 }}>{tone.w}</span>
              {' · '}{idx.name}{' '}
              <span className="mono" style={{ color: pct >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
              </span>
              {breadth != null && <>{' · '}<span title={TERMS.breadth} style={{ cursor: 'help', textDecoration: 'underline dotted', textDecorationColor: 'var(--text-faint)', textUnderlineOffset: 3 }}>{breadth}% advancing</span></>}
              {vixWord && <>{' · '}<span title={TERMS.vix} style={{ cursor: 'help', textDecoration: 'underline dotted', textDecorationColor: 'var(--text-faint)', textUnderlineOffset: 3 }}>VIX {vixWord} at <span className="mono">{market.vix.toFixed(1)}</span></span></>}
              {'. '}
            </>
          ) : 'Loading market…  '}
          {signalCount > 0 && (
            <>
              <b style={{ color: 'var(--text-primary)' }}>{signalCount} fresh signal{signalCount === 1 ? '' : 's'}</b> ready below.
            </>
          )}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span className="live" style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
          {isLiveData() ? `${getDataSource()} · 15-min delayed` : 'Simulated'}
        </span>
        <button onClick={onRefresh} title="Refresh" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '9px 14px', borderRadius: 'var(--r-md)',
          border: '1px solid var(--border)', background: 'var(--bg-card)',
          color: 'var(--text-secondary)', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
        }}>
          <Icon name="refresh" size={13} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>
    </header>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * 2 · IndexStrip — index cards
 * ══════════════════════════════════════════════════════════════════════ */
function IndexStrip({ indices }) {
  // Skeletons preserve the row height so the page doesn't reflow when data lands.
  const cards = (indices && indices.length > 0)
    ? indices
    : Array.from({ length: 4 }, (_, i) => ({ __sk: true, ticker: 'sk' + i }));
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 'var(--gap)',
      marginBottom: 'var(--section-gap)',
    }}>
      {cards.map((ix) => {
        if (ix.__sk) return (
          <div key={ix.ticker} className="card" style={{ padding: '13px 16px' }}>
            <div className="skeleton skeleton-text short" style={{ marginBottom: 8 }} />
            <div className="skeleton" style={{ height: 18, width: '60%', borderRadius: 4 }} />
          </div>
        );
        const up = (ix.changePct ?? 0) >= 0;
        return (
          <div key={ix.ticker} className="card" style={{
            padding: '13px 16px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 10,
          }}>
            <div style={{ minWidth: 0 }}>
              <div className="eyebrow" style={{ marginBottom: 4 }}>{ix.name}</div>
              <div className="mono" style={{
                fontSize: 18, fontWeight: 700, color: 'var(--text-primary)',
                lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {cs}{(ix.price ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
              color: up ? 'var(--green)' : 'var(--red)',
            }}>
              <Icon name={up ? 'arrowUR' : 'arrowDR'} size={13} stroke={2.6} />
              <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>
                {up ? '+' : ''}{(ix.changePct ?? 0).toFixed(2)}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * 3 · SignalsHero — featured top pick + ranked list
 * ══════════════════════════════════════════════════════════════════════ */

// PriceRail moved to src/components/ui/PriceRail.jsx so the TradingPage's
// position cards can reuse the same visual language.

function FeaturedSignal({ s, onTrade, onDetails }) {
  if (!s) return (
    <div className="card" style={{
      padding: 'calc(var(--card-pad) + 2px)', minHeight: 360,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-muted)', fontSize: 13,
    }}>No signals available right now — refresh in a moment.</div>
  );
  const isLong  = s.direction === 'LONG';
  const conf    = Math.round(Number(s.confidence) || 0);
  const reasons = (s.reasons || []).slice(0, 3).join(' · ') || 'Active setup';
  const rr      = s.riskReward != null ? s.riskReward : (s.targetPct && s.stopPct ? Math.abs(s.targetPct / s.stopPct).toFixed(2) : '—');
  const swing   = s.holdingDays || (s.timeframe?.match(/\d+/)?.[0]) || 5;
  const change  = Number(s.changePct) || 0;
  const isValidated = s._confidenceSource === 'backtest';

  return (
    <div className="card" style={{
      padding: 'calc(var(--card-pad) + 2px)',
      display: 'flex', flexDirection: 'column',
      position: 'relative', overflow: 'hidden',
      boxShadow: 'var(--shadow-hero)',
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(120% 80% at 0% 0%, ${isLong ? 'var(--green-dim)' : 'var(--red-dim)'} 0%, transparent 55%)`,
      }} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14, gap: 8, flexWrap: 'wrap',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--amber)',
          }}>
            <Icon name="flame" size={13} color="var(--amber)" fill="var(--amber-dim)" />
            Top pick today
          </span>
          {isValidated ? (
            <span
              title={s._backtestResult
                ? `Backtest: ${s._backtestResult.wins}W / ${s._backtestResult.losses}L over ${s._backtestResult.trials} trials · WR ${s._backtestResult.winRate}% · Sharpe ${s._backtestResult.sharpe}`
                : 'Backtested against the last 90 days of real price history.'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: 10, fontWeight: 700, padding: '3px 8px',
                borderRadius: 'var(--r-pill)',
                background: 'var(--green-dim)', color: 'var(--green-soft)',
                border: '1px solid rgba(34,197,94,0.3)',
              }}>
              <Icon name="check" size={11} stroke={2.4} /> VALIDATED
              {s._backtestResult?.trials != null && (
                <span style={{ opacity: 0.8, fontWeight: 600 }}>
                  · {s._backtestResult.trials} trials
                </span>
              )}
            </span>
          ) : s._confidenceSource === 'pending' ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 10, fontWeight: 700, padding: '3px 8px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--accent-dim)', color: 'var(--accent-soft)',
              border: '1px solid var(--border-strong)',
              animation: 'pulseSoft 1.4s var(--ease-in-out) infinite',
            }}>
              VALIDATING…
            </span>
          ) : null}
        </div>

        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 14, marginBottom: 14,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 'clamp(26px, 3.6vw, 34px)', fontWeight: 800,
                letterSpacing: '-0.04em', color: 'var(--text-primary)', lineHeight: 1,
              }}>{s.ticker}</span>
              <DirBadge dir={s.direction} size="lg" />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {s.name || s.ticker}
              {s.category && ` · ${s.category}`}
            </div>
          </div>
          <span title={buildConfidenceTitle(s) || TERMS.confidence} style={{ cursor: 'help' }}>
            <ConfRing value={conf} size={88} stroke={7} />
          </span>
        </div>

        <p style={{
          fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-secondary)',
          marginBottom: 16, minHeight: 40,
        }}>
          {s.primaryReasonCluster && (
            <span title={TERMS.reasonCluster} style={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'capitalize', cursor: 'help' }}>
              {s.primaryReasonCluster} setup.{' '}
            </span>
          )}
          {reasons}.
        </p>

        <PriceRail
          entry={Number(s.currentPrice)}
          target={Number(s.targetPrice)}
          stop={Number(s.stopLoss)}
          cs={cs}
        />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <span
            title={(() => {
              const r = Number(rr);
              if (!Number.isFinite(r) || r <= 0) return 'Reward / Risk ratio — undefined for this signal.';
              const breakeven = Math.round(100 / (1 + r));
              return `Reward / Risk = ${r.toFixed(2)}. If target hits you make ${r.toFixed(2)}× what you'd lose at stop. Breakeven win rate at this R:R is ~${breakeven}% — anything above that is positive expected value.`;
            })()}
            style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--accent-dim)', color: 'var(--accent-soft)',
              border: '1px solid var(--border-strong)',
              cursor: 'help',
            }}>R:R {rr}</span>
          <span title={TERMS.swing} style={{
            fontSize: 10, fontWeight: 700, padding: '3px 8px',
            borderRadius: 'var(--r-pill)',
            background: 'var(--bg-elevated)', color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            cursor: 'help',
          }}>~{swing}d swing</span>
          <span title={TERMS.todayChange} style={{
            fontSize: 10, fontWeight: 700, padding: '3px 8px',
            borderRadius: 'var(--r-pill)',
            background: change >= 0 ? 'var(--green-dim)' : 'var(--red-dim)',
            color:      change >= 0 ? 'var(--green-soft)' : 'var(--red-soft)',
            border:     `1px solid ${change >= 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
            cursor: 'help',
          }}>{change >= 0 ? '+' : ''}{change.toFixed(2)}% today</span>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 'auto', flexWrap: 'wrap' }}>
          <button onClick={() => onTrade(s)} style={{
            flex: '1 1 180px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            padding: '13px 18px', borderRadius: 'var(--r-md)',
            border: 'none', background: 'var(--grad-accent)',
            color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow: '0 6px 20px var(--accent-glow)',
          }}>
            <Icon name="zap" size={15} fill="#fff" color="#fff" /> Trade {s.ticker}
          </button>
          <button onClick={() => onDetails(s)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '13px 16px', borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)', background: 'var(--bg-card)',
            color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Analysis <Icon name="arrowR" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SignalsHero({ signals, filter, setFilter, onTrade, onDetails, onAllSignals }) {
  const directional = signals.filter(s => s.direction !== 'HOLD');
  const longN  = directional.filter(s => s.direction === 'LONG').length;
  const shortN = directional.filter(s => s.direction === 'SHORT').length;
  const filtered = directional
    .filter(s => filter === 'ALL' ? true : s.direction === filter)
    // Sort by displayed confidence DESC, score tiebreaker (matches commit 9caec2d)
    .sort((a, b) => {
      const c = (b.confidence ?? 0) - (a.confidence ?? 0);
      if (c !== 0) return c;
      return (b.score ?? 0) - (a.score ?? 0);
    });
  const featured = filtered[0];
  const FILTERS = [
    ['ALL',   `All ${directional.length}`],
    ['LONG',  `Long ${longN}`],
    ['SHORT', `Short ${shortN}`],
  ];

  return (
    <section style={{ marginBottom: 'var(--section-gap)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 30, height: 30, borderRadius: 'var(--r-md)',
            background: 'var(--amber-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon name="zap" size={16} color="var(--amber)" fill="var(--amber)" />
          </span>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0 }}>
              Today's Signals
            </h2>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
              Ranked by confidence · full NYSE + NASDAQ scan
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex', gap: 3, padding: 3,
            background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-md)',
          }}>
            {FILTERS.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                padding: '6px 12px', borderRadius: 'var(--r-sm)',
                border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
                background: filter === k ? 'var(--accent-dim)' : 'transparent',
                color:      filter === k ? 'var(--accent-soft)' : 'var(--text-muted)',
                fontFamily: 'inherit',
              }}>{label}</button>
            ))}
          </div>
          <button onClick={onAllSignals} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '8px 13px', borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)', background: 'var(--bg-card)',
            color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            View all <Icon name="arrowR" size={12} />
          </button>
        </div>
      </div>

      <div className="hero-grid">
        <FeaturedSignal s={featured} onTrade={onTrade} onDetails={onDetails} />
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * 4 · Trust band — PortfolioCard (everyone) + TrackRecordCard (admin only)
 * ══════════════════════════════════════════════════════════════════════ */
function PortfolioCard({ data, onTrade }) {
  const loading = !data;
  const up = data && data.pl >= 0;
  return (
    <div className="card" style={{ padding: 'var(--card-pad)', minWidth: 0 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, gap: 8,
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 26, height: 26, borderRadius: 'var(--r-sm)',
            background: 'var(--accent-dim)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="wallet" size={14} color="var(--accent-soft)" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>Your Portfolio</span>
        </span>
        <button onClick={onTrade} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '6px 12px', borderRadius: 'var(--r-pill)',
          border: '1px solid var(--border)', background: 'transparent',
          color: 'var(--accent-soft)', fontSize: 11.5, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Trade <Icon name="chevR" size={12} />
        </button>
      </div>

      {loading ? (
        <>
          <div className="skeleton" style={{ height: 28, width: 160, borderRadius: 6, marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 14, width: 180, borderRadius: 4 }} />
        </>
      ) : (
        <>
          <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
          }}>
            <div>
              <div className="eyebrow" title={TERMS.totalValue} style={{ marginBottom: 5, cursor: 'help' }}>Total value</div>
              <div className="mono" style={{
                fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em',
                color: 'var(--text-primary)', lineHeight: 1,
              }}>{fmtMoney(data.totalValue)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span title={up ? TERMS.realizedPnl + ' Combined realised + unrealised since the portfolio started.' : TERMS.realizedPnl} className="mono" style={{
                  fontSize: 13, fontWeight: 700,
                  color: up ? 'var(--green)' : 'var(--red)',
                  cursor: 'help',
                }}>
                  {up ? '+' : '−'}{fmtMoney(data.pl)} ({up ? '+' : ''}{data.plPct.toFixed(2)}%)
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>all-time</span>
              </div>
            </div>
            {data.curve?.length > 1 && (
              <div style={{ flex: '1 1 140px', minWidth: 120, maxWidth: 240 }}>
                <AreaSpark
                  data={data.curve}
                  color={up ? 'var(--green)' : 'var(--red)'}
                  id="pf"
                  h={56}
                />
              </div>
            )}
          </div>

          <div style={{
            display: 'flex', gap: 18, marginTop: 16, paddingTop: 14,
            borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap',
          }}>
            {[
              ['Today',          data.todayChange != null ? `${data.todayChange >= 0 ? '+' : '−'}${fmtMoney(data.todayChange)}` : '—', data.todayChange != null && data.todayChange >= 0 ? 'var(--green)' : 'var(--red)', 'Net change in total portfolio value from the prior daily snapshot. Includes both realised closes and unrealised mark-to-market.'],
              ['Cash',           fmtMoney(data.cash),               'var(--text-primary)', TERMS.cash],
              ['Open positions', String(data.openCount),            'var(--text-primary)', TERMS.openPositions],
            ].map(([l, v, c, tip]) => (
              <div key={l} title={tip} style={{ cursor: 'help' }}>
                <div className="eyebrow" style={{ marginBottom: 3 }}>{l}</div>
                <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          {data.positions?.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.positions.slice(0, 4).map((pos) => {
                const pup = (pos.pl ?? 0) >= 0;
                return (
                  <div key={pos.id} style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 60px) auto 1fr auto',
                    alignItems: 'center', gap: 10, padding: '8px 4px',
                  }}>
                    <span style={{
                      fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{pos.ticker}</span>
                    <DirBadge dir={pos.dir} size="sm" />
                    <HealthDot state={pos.health || 'HOLD'} />
                    <span className="mono" style={{
                      fontSize: 12.5, fontWeight: 700,
                      color: pup ? 'var(--green)' : 'var(--red)', textAlign: 'right',
                    }}>{pup ? '+' : '−'}{fmtMoney(pos.pl || 0)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// TrackRecordCard moved to src/components/TrackRecordCard.jsx and surfaced
// in the Admin Page's Signal Engine tab — end users no longer see aggregate
// signal performance from the Dashboard.

/* ════════════════════════════════════════════════════════════════════════
 * 5 · MarketContext — SPY area chart + internals + Gainers/Losers
 * ══════════════════════════════════════════════════════════════════════ */
function MarketContext({ market, indexHistory }) {
  const closes = (indexHistory || []).map(b => b.close).filter(Number.isFinite);
  const spyUp = closes.length >= 2 && closes[closes.length - 1] >= closes[0];
  const last  = closes[closes.length - 1];
  const first = closes[0];
  const pct   = (last != null && first != null && first > 0) ? ((last - first) / first) * 100 : null;

  // Fear/Greed bucketing: 0-25 extreme fear, 26-45 fear, 46-55 neutral,
  // 56-75 greed, 76-100 extreme greed. Colours follow the CNN convention.
  const fg = market?.fearGreedIndex;
  const fgColor = fg == null ? 'var(--text-muted)'
                : fg < 26 ? 'var(--red)'
                : fg < 46 ? '#fb923c'
                : fg < 56 ? 'var(--amber)'
                : fg < 76 ? '#84cc16'
                : 'var(--green)';

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{
          width: 30, height: 30, borderRadius: 'var(--r-md)',
          background: 'var(--bg-elevated)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="layers" size={15} color="var(--text-secondary)" />
        </span>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0 }}>
            Market Context
          </h2>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
            The backdrop behind today's signals
          </div>
        </div>
      </div>

      <div>
        {/* SPY chart + internals */}
        <div className="card" style={{ padding: 'var(--card-pad)', minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 14, gap: 8, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                {market?.indices?.[0]?.name || 'S&P 500'} · 30 days
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {market?.indices?.[0]?.ticker || 'SPY'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
                {last != null ? `${cs}${last.toFixed(2)}` : '—'}
              </div>
              {pct != null && (
                <div className="mono" style={{
                  fontSize: 11, fontWeight: 700,
                  color: spyUp ? 'var(--green)' : 'var(--red)',
                }}>{spyUp ? '+' : ''}{pct.toFixed(2)}%</div>
              )}
            </div>
          </div>

          {closes.length > 1 ? (
            <AreaSpark data={closes} color={spyUp ? 'var(--green)' : 'var(--red)'} id="spy" h={120} />
          ) : (
            <div className="skeleton" style={{ height: 120, width: '100%', borderRadius: 'var(--r-md)' }} />
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            {[
              { l: 'Sentiment',
                v: market?.sentiment ?? '—',
                sub: 'SPY day move',
                tip: 'Today\'s SPY % change bucketed into Bullish (>+0.5%), Bearish (<−0.5%), or Neutral.',
                c: market?.sentiment === 'Bullish' ? 'var(--green)' : market?.sentiment === 'Bearish' ? 'var(--red)' : 'var(--amber)' },
              { l: 'VIX',
                v: market?.vix != null ? market.vix.toFixed(1) : '—',
                // VIX regime bands match the signal scanner: <18 calm, 18-25 normal, ≥25 stressed.
                sub: market?.vix == null ? 'unavailable'
                   : market.vix < 18 ? 'calm'
                   : market.vix < 25 ? 'normal'
                   : 'stressed',
                tip: 'CBOE Volatility Index — the market\'s 30-day expected move on SPX, derived from option prices. Below 18 the signal engine eases its gates (more setups ship); above 25 it tightens them (only the highest-conviction setups pass). The latest daily close from Polygon, refreshed every 5 min.',
                c: market?.vix == null ? 'var(--text-muted)'
                 : market.vix < 18 ? 'var(--green)'
                 : market.vix < 25 ? 'var(--amber)'
                 : 'var(--red)' },
              { l: 'Fear/Greed',
                v: fg != null ? String(fg) : '—',
                sub: fg == null ? '—'
                   : fg < 26 ? 'extreme fear'
                   : fg < 46 ? 'fear'
                   : fg < 56 ? 'neutral'
                   : fg < 76 ? 'greed'
                   : 'extreme greed',
                tip: '3-factor proxy centred at 50: VIX (inverse), market breadth, and today\'s SPY move. Inspired by CNN\'s Fear & Greed Index but computed from data we already fetch. Extreme fear (<26) and extreme greed (>75) historically mark short-term turning points.',
                c: fgColor },
              { l: 'Breadth',
                v: market?.advancers != null && market?.decliners != null
                    ? `${market.advancers.toLocaleString()}↑ ${market.decliners.toLocaleString()}↓`
                    : '—',
                sub: market?.advancers != null && market?.decliners != null
                    ? `${Math.round(market.advancers / (market.advancers + market.decliners) * 100)}% up`
                    : '—',
                tip: 'Today\'s grouped-daily snapshot across every liquid US stock (price ≥$5, vol ≥500k). >55% up = broad rally, <45% up = broad selloff. Narrow rallies (mega-cap only) often fade fast.',
                c: market?.advancers != null && market?.decliners != null
                    ? (market.advancers >= market.decliners ? 'var(--green)' : 'var(--red)')
                    : 'var(--text-secondary)' },
            ].map((m) => (
              <div key={m.l} title={m.tip} style={{
                flex: '1 1 auto', minWidth: 84,
                padding: '8px 10px', borderRadius: 'var(--r-sm)',
                background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
                cursor: 'help',
              }}>
                <div className="eyebrow" style={{ marginBottom: 3 }}>{m.l}</div>
                <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: m.c, whiteSpace: 'nowrap' }}>
                  {m.v}
                </div>
                {m.sub && (
                  <div style={{
                    fontSize: 10, color: 'var(--text-muted)', marginTop: 2,
                    textTransform: 'lowercase', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{m.sub}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════════
 * Composer — DashboardPage
 * ══════════════════════════════════════════════════════════════════════ */
export default function DashboardPage() {
  const { setSelectedTicker, openTrade } = useOutletContext();
  const navigate = useNavigate();
  // user is read for the OrientationHeader greeting only; the TrackRecordCard
  // gate that used to live here moved to AdminPage in b5fc7fe.
  const { user } = useAuth();
  const { config } = useExchange();

  const [signals, setSignals] = useState([]);
  const [market, setMarket] = useState(null);
  const [indexHistory, setIndexHistory] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('ALL');

  // Cancel-flag for the in-flight validation pass
  const dashCancelRef = useRef(null);
  useEffect(() => () => { if (dashCancelRef.current) dashCancelRef.current(); }, []);

  // Reliability curve fetched once on mount; applied as the final transform
  // on each validated signal so the displayed confidence is the historically
  // observed WR. Empty array = identity (pre-T1 behaviour).
  const calibrationCurveRef = useRef([]);
  useEffect(() => {
    apiGetCalibrationCurvePublic().then(curve => { calibrationCurveRef.current = curve; });
  }, []);

  const loadData = useCallback(async () => {
    setRefreshing(true);
    try {
      const mkt = await getMarketOverview();
      setMarket(mkt);

      // Index history for the SPY 30-day area chart
      const firstIndex = config.indices?.[0]?.ticker || 'SPY';
      const hist = await getHistory(firstIndex, 30).catch(() => null);
      if (hist) setIndexHistory(hist);

      // Portfolio + 7-day history for the trust-band card
      Promise.all([
        apiGetPortfolio(),
        apiGetPortfolioHistory(7).catch(() => ({ history: [] })),
      ]).then(([p, h]) => {
        const initial    = parseFloat(p.portfolio.initial_balance ?? p.portfolio.cash_balance);
        const cash       = parseFloat(p.portfolio.cash_balance);
        const open       = p.openTrades || [];
        // Live total = cash + sum(mark-to-market of open positions), computed
        // server-side so we don't depend on the latest portfolio_history
        // snapshot (which only updates on trade open/close and goes stale
        // between trades — that's why opening a new position previously
        // appeared to "bump" the total).
        const totalValue = p.totalValue != null
          ? parseFloat(p.totalValue)
          : (parseFloat(p.positionsValue) || 0) + cash;
        const pl         = totalValue - initial;
        const plPct      = initial > 0 ? (pl / initial) * 100 : 0;
        const curve      = (h.history || []).map(r => parseFloat(r.total_value)).filter(Number.isFinite);
        // "Today change" still comes from the snapshot history — it's a
        // running record of prior days' values, separate from the live
        // totalValue we now show in the headline.
        const todayChange = h.history?.length >= 2
          ? parseFloat(h.history[h.history.length - 1].total_value) - parseFloat(h.history[h.history.length - 2].total_value)
          : null;
        // Map open trades into the portfolio-card position shape
        const positions = open.slice(0, 6).map(t => {
          const qty   = parseFloat(t.quantity) || 0;
          const entry = parseFloat(t.price)    || 0;
          const dir   = t.direction === 'SHORT' || t.direction === 'COVER' ? 'SHORT' : 'LONG';
          return {
            id: t.id, ticker: t.ticker, dir,
            pl: parseFloat(t.unrealized_pnl) || 0,
            health: t.last_health_status === 'exit' ? 'CONSIDER EXIT'
                  : t.last_health_status === 'monitor' ? 'MONITOR' : 'HOLD',
          };
        });
        setPortfolio({ cash, openCount: open.length, totalValue, pl, plPct, initial, curve, todayChange, positions });
      }).catch(() => setPortfolio({ cash: 0, openCount: 0, totalValue: 0, pl: 0, plPct: 0, initial: 0, curve: [], todayChange: null, positions: [] }));

      // Generate + auto-validate today's signals. We kick off the curve
      // fetch + signal scan in parallel, then await BOTH before starting
      // validation — otherwise fast-cached backtest callbacks fire while
      // the curve is still loading and the conf displayed stays at the
      // raw heuristic value instead of the observed-WR mapping.
      const [sigs, curve] = await Promise.all([
        generateTradeSignals(25),
        apiGetCalibrationCurvePublic(),
      ]);
      calibrationCurveRef.current = curve;
      const tagged = sigs.map(s => s.direction === 'HOLD' ? s : { ...s, _confidenceSource: 'pending' });
      setSignals(tagged);

      if (dashCancelRef.current) dashCancelRef.current();
      let cancelled = false;
      dashCancelRef.current = () => { cancelled = true; };
      validateSignalsBatch(
        tagged,
        ({ ticker, status, result }) => {
          if (cancelled) return;
          setSignals(prev => prev.map(s => {
            if (s.ticker !== ticker) return s;
            if (status === 'validated') {
              const preCurve = result.calibratedConfidence;
              // Use the local `curve` from the closure, not the ref —
              // guarantees the curve we just awaited is the one applied,
              // regardless of what overwrites the ref later.
              const { mapped, bin } = applyCalibrationCurve(preCurve, curve);
              return {
                ...s,
                confidence: mapped,
                calibratedConfidence: mapped,
                preCurveConfidence: preCurve,
                reliabilityBin: bin,
                _confidenceSource: 'backtest',
                _backtestResult: result,
              };
            }
            return { ...s, _confidenceSource: status };
          }));
        },
        { isCancelled: () => cancelled }
      );
    } catch (e) {
      console.error('Failed to load Overview data:', e);
    }
    setRefreshing(false);
  // Reload on mount only; the interval below covers periodic refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const iv = setInterval(loadData, 2 * 60 * 1000);   // 2-min refresh — Polygon limits are generous
    return () => clearInterval(iv);
  }, [loadData]);

  const directionalCount = signals.filter(s => s.direction !== 'HOLD').length;

  // Click handlers: signals lead to the detail modal; the Trade CTA opens
  // the Trading page (Execution page lands in slice 5).
  // Open the shared NewTradeModal at Layout level — keeps the user on the
  // Overview page until they actually click Execute. Defaults targetPrice
  // from the signal so the take-profit field prefills correctly.
  const handleTrade   = useCallback((s) => openTrade({
    ticker:       s.ticker,
    direction:    s.direction === 'LONG' ? 'BUY' : 'SHORT',
    price:        s.currentPrice,
    type:         'stock',
    stopPrice:    s.stopLoss,
    targetPrice:  s.targetPrice,
    swingDays:    s.holdingDays,
    signalSource: `AI Signal · ${s.timeframe ?? ''}`,
    // Kelly inputs for NewTradeModal's sizing.
    confidence:   s.confidence,
    riskReward:   s.riskReward,
  }), [openTrade]);
  const handleDetails = useCallback((s) => setSelectedTicker(s.ticker), [setSelectedTicker]);

  return (
    <div className="fade-up">
      <OrientationHeader
        user={user}
        market={market}
        signalCount={directionalCount}
        onRefresh={loadData}
        refreshing={refreshing}
      />
      <IndexStrip indices={market?.indices} />
      <SignalsHero
        signals={signals}
        filter={filter}
        setFilter={setFilter}
        onTrade={handleTrade}
        onDetails={handleDetails}
        onAllSignals={() => navigate('/dashboard/signals')}
      />
      {/* Trust band — TrackRecordCard moved to Admin Page's Signal Engine
          tab, so end users (and admins) see only the portfolio card here.
          Admin can still review the aggregate signal performance numbers
          in the dedicated admin tab. */}
      <div style={{ marginBottom: 'var(--section-gap)' }}>
        <PortfolioCard data={portfolio} onTrade={() => navigate('/dashboard/trading')} />
      </div>
      <MarketContext
        market={market}
        indexHistory={indexHistory}
      />

      <style>{`
        /* Responsive — match spec: ≤980 hero+ctx+trust collapse to 1col */
        @media (max-width: 980px) {
          .hero-grid, .ctx-grid, .trust-grid { grid-template-columns: 1fr !important; }
        }
        /* Compress dense signal rows on phones so nothing overflows */
        @media (max-width: 620px) {
          .sig-row {
            grid-template-columns: minmax(0, 1fr) auto auto !important;
            row-gap: 6px !important;
          }
          .sig-row > :nth-child(4), .sig-row > .row-prices { display: none !important; }
          .sig-row > .row-trade { grid-column: 3 / span 1; }
        }
        .sig-row:hover { background: var(--bg-surface); }
        .row-trade:hover { background: var(--accent-dim); border-color: var(--border-strong); }
      `}</style>
    </div>
  );
}
